import type { TOptions } from "i18next";
import { AlertTriangle, Check, Copy, ExternalLink, Info, KeyRound, Lock, Mail, Plus } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaEmpty } from "@/shared/components/feedback/ColocaEmpty";
import { ColocaSkeleton } from "@/shared/components/feedback/ColocaSkeleton";
import { ColocaSearchInput } from "@/shared/components/forms/ColocaSearchInput";
import { ColocaSelect } from "@/shared/components/forms/ColocaSelect";
import type { SettingsTranslations } from "@/shared/lang/interfaces/settings";
import { useTranslations } from "@/shared/lang/useTranslations";
import { ConfigService } from "@/shared/services/config.service";
import { colocaToast, toastApiError } from "@/shared/utils/toast";

import { useAccessCan } from "../../hooks/useAccessCan";
import { useApiKeysData, useRolesData } from "../../hooks/useAccessData";
import { useEffectiveSummaries } from "../../hooks/useEffectiveSummaries";
import type { AllowedIp, ApiKeyEntity, CreateApiKeyResponse } from "../../services/apiKeysService";
import { CreateApiKeyForm } from "../CreateApiKeyForm";
import { DisableApiKeyDialog } from "../DisableApiKeyDialog";
import { EditApiKeyIpsDialog } from "../EditApiKeyIpsDialog";
import { SettingsSection } from "../SettingsSection";
import { AccessApiKeysCards } from "./AccessApiKeysCards";
import type { AccessApiKeysListProps } from "./accessApiKeysList.types";
import { AccessApiKeysTable } from "./AccessApiKeysTable";
import { AccessHeaderStat } from "./AccessHeaderStat";
import { AssignRolesDrawer } from "./AssignRolesDrawer";

const API_DOCS_URL = new URL("/coloca/docs/public", ConfigService.getInstance().getOrThrow("colocaApiUrl"))
  .href;

const API_KEYS_CONTACT_EMAIL = "soporte@colocapayments.com";

const isAdminKey = (key: ApiKeyEntity): boolean => key.is_admin || key.super_admin;

type SettingsT = <K extends keyof SettingsTranslations>(
  key: K,
  options?: TOptions,
) => SettingsTranslations[K];

// Module-level (not inside the component) so the unavoidable `Date.now()` call
// is not flagged as an impure call during render.
function formatLastUsed(value: string | null | undefined, t: SettingsT): string | null {
  if (!value) return null;
  const min = Math.floor((Date.now() - new Date(value).getTime()) / 60000);
  if (min < 1) return t("relative_time_now");
  if (min < 60) return t("relative_time_minutes", { n: min });
  const hours = Math.floor(min / 60);
  if (hours < 24) return t("relative_time_hours", { n: hours });
  const days = Math.floor(hours / 24);
  if (days < 30) return t("relative_time_days", { n: days });
  return new Date(value).toLocaleDateString();
}

/**
 * The single "API Keys" section. Covers the full backend surface for keys:
 *
 * - **Lifecycle:** create (+ one-time secret), enable/disable, allowed IPs.
 * - **Authorization (RBAC):** assigned roles + effective access resolved deny-first, assign/remove with
 *   auto-save.
 *
 * The backend authorizes keys by their roles, not by the legacy `scopes[]` array — so we never present scopes
 * as live permissions. Scopes appear only in the create form, because `POST /api-keys` requires them and
 * homologates them into base roles; after creation the key is managed purely by roles. There is no `is_admin`
 * bypass: admin keys carry the `full_admin_access` role, so their full access shows up as the whole catalog
 * (`TOTAL_CELLS`) through the normal evaluation. The `is_admin` / `super_admin` flags only drive an
 * informational badge.
 *
 * Gating: read = `api_key:read`; create, enable/disable/IPs and role changes = `api_key:write` (plus
 * `roles:write` for role changes). Atoms, not the `:update` the endpoints declare: `scopes[]` carries the
 * RBAC projection, which emits read/write/delete only, and the guard collapses `update` to `write`.
 */
export function ApiKeysAccessSection() {
  const { t } = useTranslations("settings");
  const { canAll } = useAccessCan();

  // The catalog's own state matters: with `api_key:read` but not `roles:read` the catalog 403s, and resolving
  // summaries against an empty catalog would render every key as an authoritative-looking 0/N. Degrade the
  // authorization surface instead — the key lifecycle (create/enable/disable/IPs) does not depend on it.
  const { roles, isLoading: catalogLoading, hasNoAccess: catalogUnavailable } = useRolesData();
  const {
    keys,
    total,
    rolesByKey,
    rolesUnavailable,
    isLoading,
    hasNoAccess,
    load,
    assignRoles,
    removeRole,
    create,
    disable,
    enable,
    updateAllowedIps,
    getCurrentIp,
  } = useApiKeysData();

  const [query, setQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [assignTarget, setAssignTarget] = useState<ApiKeyEntity | null>(null);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [justCreated, setJustCreated] = useState<CreateApiKeyResponse | null>(null);
  const [copiedKey, setCopiedKey] = useState(false);
  const [disableTarget, setDisableTarget] = useState<ApiKeyEntity | null>(null);
  const [editIpsTarget, setEditIpsTarget] = useState<ApiKeyEntity | null>(null);

  // `:write`, not `:update` — `scopes[]` carries RBAC atoms, and the backend collapses `update` to
  // `write` before evaluating, so the atom is what the guard actually requires.
  const canCreate = canAll("api_key:write");
  const canUpdate = canAll("api_key:write");
  const canManageRoles = canAll("api_key:write", "roles:write");

  const reload = () => load().catch(toastApiError);

  // One aggregated warning per load when some keys' roles couldn't be fetched, so an incomplete table is
  // never presented as authoritative truth. Keyed on the set instance — fires once per load, not per render.
  useEffect(() => {
    if (rolesUnavailable.size > 0) {
      colocaToast.warning(t("access_roles_partial_fail", { count: rolesUnavailable.size }));
    }
  }, [rolesUnavailable, t]);

  // A key with zero roles genuinely has no access (default-deny). Admin keys
  // carry the `full_admin_access` role, so they are not counted here. A failed
  // roles fetch is "unknown", not a confirmed zero — exclude it from the tally.
  const noRoleCount = useMemo(
    () => keys.filter((k) => !rolesUnavailable.has(k.id) && (rolesByKey[k.id]?.length ?? 0) === 0).length,
    [keys, rolesByKey, rolesUnavailable],
  );

  // Effective-access summaries memoized per principal (not recomputed on every keystroke). See Finding #5.
  const summaryById = useEffectiveSummaries(rolesByKey, roles);

  const roleOptions = useMemo(
    () => [
      { value: "all", label: t("access_filter_all_roles") },
      ...roles.map((role) => ({ value: role.id, label: role.name })),
    ],
    [roles, t],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return keys.filter((k) => {
      const assigned = rolesByKey[k.id] ?? [];
      const matchesRole = roleFilter === "all" || assigned.some((r) => r.id === roleFilter);
      const matchesQuery = !q || k.name.toLowerCase().includes(q) || k.key.toLowerCase().includes(q);
      return matchesRole && matchesQuery;
    });
  }, [keys, rolesByKey, query, roleFilter]);

  const targetAssignedIds = useMemo(
    () => (assignTarget ? (rolesByKey[assignTarget.id] ?? []).map((r) => r.id) : []),
    [assignTarget, rolesByKey],
  );

  // One object for both faces of the list, so the table and the cards cannot be fed different data.
  const listProps: AccessApiKeysListProps = {
    keys: filtered,
    rolesByKey,
    rolesUnavailable,
    summaryById,
    catalogUnavailable,
    canManageRoles,
    canUpdate,
    lastUsedLabel: (key) => formatLastUsed(key.last_used_at, t),
    onOpenRoles: setAssignTarget,
    onEditIps: setEditIpsTarget,
    onDisable: setDisableTarget,
    onEnable: (key) => void handleEnable(key),
  };

  const handleCreated = async (result: CreateApiKeyResponse) => {
    setIsCreateOpen(false);
    setJustCreated(result);
    setCopiedKey(false);
    colocaToast.success(t("api_keys_create_success"));
    await reload();
  };

  const handleCopyKey = () => {
    if (!justCreated) return;
    navigator.clipboard
      .writeText(justCreated.plainKey)
      .then(() => {
        setCopiedKey(true);
        setTimeout(() => setCopiedKey(false), 2000);
      })
      .catch(() => {});
  };

  const handleEnable = async (key: ApiKeyEntity) => {
    try {
      await enable(key.id);
      colocaToast.success(t("api_keys_enable_success"));
      await reload();
    } catch (error) {
      toastApiError(error);
    }
  };

  const handleDisableConfirm = async () => {
    if (!disableTarget) return;
    try {
      await disable(disableTarget.id);
      colocaToast.success(t("api_keys_disable_success"));
      setDisableTarget(null);
      await reload();
    } catch (error) {
      toastApiError(error);
    }
  };

  const handleSaveIps = async (ips: AllowedIp[]) => {
    if (!editIpsTarget) return;
    try {
      await updateAllowedIps(editIpsTarget.id, ips);
      colocaToast.success(t("api_keys_update_success"));
      setEditIpsTarget(null);
    } catch (error) {
      toastApiError(error);
    }
  };

  return (
    <SettingsSection
      id="apikeys-acceso"
      eyebrow={t("access_eyebrow")}
      title={t("access_apikeys_title")}
      sub={t("access_apikeys_sub")}
      headerRight={
        !isLoading && !hasNoAccess ? (
          <div className="flex items-center gap-3.5">
            <AccessHeaderStat label={t("access_apikeys_stat_keys")} value={keys.length} />
            {noRoleCount > 0 && (
              <AccessHeaderStat label={t("access_stat_no_roles")} value={noRoleCount} danger />
            )}
          </div>
        ) : undefined
      }
    >
      {hasNoAccess ? (
        <ColocaEmpty
          surface="card"
          icon={Lock}
          title={t("access_no_access_title")}
          description={t("access_no_access_description")}
        />
      ) : (
        <>
          {/* The roles catalog 403'd (`roles:read` missing): effective access cannot be resolved, so it is
              shown as unknown ("?") — never as an authoritative 0/N — and role management is disabled. The
              key lifecycle below (create/enable/disable/IPs) stays available. */}
          {catalogUnavailable && (
            <div className="mb-3.5 flex items-start gap-2.5 rounded-md border border-warning/40 bg-warning/10 px-3 py-2.5 text-[12px] leading-relaxed text-foreground">
              <AlertTriangle size={13} className="mt-0.5 shrink-0 text-warning" />
              {t("access_catalog_unavailable")}
            </div>
          )}

          {/* Toolbar: search + role filter (left), docs + create (right). The widths are fixed from `sm`
              up, which is the layout this was designed for; below that each control takes the full width
              instead of wrapping into ragged lines with dead space on the right. Every mobile class has an
              `sm:` counterpart, so from 640px nothing changes.

              The full width has to be claimed by the BUTTONS, not only by the row that holds them: a
              `ColocaButton` is `shrink-0 whitespace-nowrap` on purpose — truncating a label is worse than
              overflowing — so a width on the container constrains nothing and the row simply spills past
              the card. Two labels of this length need 293px in Spanish and 305px in Portuguese against the
              266px a phone affords, which is why they also stack below `sm`. */}
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <div className="w-full sm:min-w-[200px] sm:max-w-[280px] sm:flex-1">
              <ColocaSearchInput
                value={query}
                onValueChange={setQuery}
                placeholder={t("access_apikeys_search_placeholder")}
              />
            </div>
            {!catalogUnavailable && (
              <div className="w-full sm:w-[180px]">
                <ColocaSelect value={roleFilter} onValueChange={setRoleFilter} options={roleOptions} />
              </div>
            )}
            <div className="flex w-full flex-col items-stretch gap-2 sm:ml-auto sm:w-auto sm:flex-row sm:items-center">
              <ColocaButton
                variant="default"
                size="sm"
                iconTrailing={ExternalLink}
                onClick={() => window.open(API_DOCS_URL, "_blank", "noopener,noreferrer")}
                className="w-full sm:w-auto"
              >
                {t("api_keys_docs_button")}
              </ColocaButton>
              {canCreate && (
                <ColocaButton
                  variant="primary"
                  size="sm"
                  icon={Plus}
                  onClick={() => {
                    setJustCreated(null);
                    setIsCreateOpen(true);
                  }}
                  className="w-full sm:w-auto"
                >
                  {t("api_keys_create_button")}
                </ColocaButton>
              )}
            </div>
          </div>

          {/* Secret shown once, right after creation */}
          {justCreated && (
            <div className="mb-3 rounded-md border border-warning/40 bg-warning/10 p-3.5">
              <div className="flex items-center gap-2 text-[13px] font-semibold text-foreground">
                <AlertTriangle size={14} className="text-warning" />
                {t("api_keys_created_banner_title")}
              </div>
              <p className="mt-1 text-[11.5px] text-muted-foreground">
                {t("api_keys_created_banner_warning")}
              </p>
              <div className="mt-2 flex items-center gap-2">
                {/* Focusable because it scrolls and holds nothing operable: this is the one and only time
                    the key is shown, so a keyboard user has to be able to reach the rest of it
                    (WCAG 2.1.1). Same treatment `ColocaDataTable` gives its overflowing scroller. */}
                <code
                  tabIndex={0}
                  role="region"
                  aria-label={t("api_keys_created_banner_title")}
                  className="min-w-0 flex-1 overflow-x-auto rounded bg-muted px-2.5 py-2 font-mono text-[12px] text-foreground"
                >
                  {justCreated.plainKey}
                </code>
                <ColocaButton
                  variant="default"
                  size="xs"
                  icon={copiedKey ? Check : Copy}
                  onClick={handleCopyKey}
                >
                  {copiedKey ? t("api_keys_created_copied") : t("api_keys_created_copy")}
                </ColocaButton>
              </div>
              <button
                type="button"
                onClick={() => setJustCreated(null)}
                className="mt-1 inline-flex min-h-6 items-center py-1 text-[11.5px] text-muted-foreground hover:text-foreground"
              >
                {t("api_keys_created_dismiss")}
              </button>
            </div>
          )}

          {/* Creation restricted — how to get it enabled */}
          {!canCreate && (
            <div className="mb-3 flex items-start gap-2.5 rounded-md border border-border bg-muted/40 p-3.5">
              <Mail size={15} className="mt-0.5 shrink-0 text-muted-foreground" />
              <div>
                <div className="text-[13px] font-semibold text-foreground">
                  {t("api_keys_no_write_title")}
                </div>
                <p className="mt-0.5 text-[12px] leading-relaxed text-muted-foreground">
                  {t("api_keys_no_write_description", { email: API_KEYS_CONTACT_EMAIL })}
                </p>
              </div>
            </div>
          )}

          {/* Inline create form (scopes are required by the backend and become
              the key's initial roles, which it can then refine below). */}
          {isCreateOpen && canCreate && (
            <CreateApiKeyForm
              onCreate={create}
              detectCurrentIp={getCurrentIp}
              onCreated={handleCreated}
              onCancel={() => setIsCreateOpen(false)}
            />
          )}

          {/* Backend holds more keys than this capped page shows — disclose it so the count, the simulator
              and the per-role member tallies are not silently under-reported. */}
          {!isLoading && total > keys.length && (
            <div className="mb-3 flex items-start gap-2.5 rounded-md border border-border bg-muted/40 p-3 text-[12px] leading-relaxed text-muted-foreground">
              <Info size={14} className="mt-0.5 shrink-0" />
              <span>{t("access_apikeys_truncated", { shown: keys.length, total })}</span>
            </div>
          )}

          {/* Table (the catalog load gates it too — summaries resolved against a half-loaded catalog would
              flash misleading 0/N counts) */}
          {isLoading || catalogLoading ? (
            <div className="space-y-2 rounded-md border border-border p-3">
              <ColocaSkeleton className="h-12 w-full" />
              <ColocaSkeleton className="h-12 w-full" />
              <ColocaSkeleton className="h-12 w-full" />
            </div>
          ) : keys.length === 0 ? (
            <ColocaEmpty surface="card" icon={KeyRound} title={t("api_keys_empty")} />
          ) : (
            /* Two faces of the same keys, picked by CSS rather than by `useIsMobile()` — the pattern the
               four sibling access surfaces already use, with no first render where `isMobile` is still
               `undefined` and resolves to desktop. Both take the same props. */
            <>
              <div className="hidden xl:block">
                <AccessApiKeysTable {...listProps} />
              </div>
              <div className="xl:hidden">
                <AccessApiKeysCards {...listProps} />
              </div>
            </>
          )}

          <div className="mt-2.5 flex items-center gap-1.5 text-[11.5px] text-muted-foreground">
            <Info size={12} />
            {t("access_apikeys_footnote")}
          </div>
        </>
      )}

      <AssignRolesDrawer
        principal={
          assignTarget
            ? { id: assignTarget.id, name: assignTarget.name, kind: "key", subtitle: assignTarget.key }
            : null
        }
        open={!!assignTarget}
        onOpenChange={(open) => {
          if (!open) setAssignTarget(null);
        }}
        roles={roles}
        assignedRoleIds={targetAssignedIds}
        canManage={canManageRoles}
        onAssign={(roleIds) => assignRoles(assignTarget!.id, roleIds)}
        onRemove={(roleId) => removeRole(assignTarget!.id, roleId)}
      />

      {disableTarget && (
        <DisableApiKeyDialog
          open={!!disableTarget}
          apiKeyName={disableTarget.name}
          apiKeyPrefix={disableTarget.key}
          onConfirm={handleDisableConfirm}
          onCancel={() => setDisableTarget(null)}
        />
      )}

      {editIpsTarget && (
        <EditApiKeyIpsDialog
          open={!!editIpsTarget}
          apiKeyName={editIpsTarget.name}
          apiKeyPrefix={editIpsTarget.key}
          initialIps={editIpsTarget.allowed_ips ?? []}
          isAdmin={isAdminKey(editIpsTarget)}
          onSave={handleSaveIps}
          onCancel={() => setEditIpsTarget(null)}
        />
      )}
    </SettingsSection>
  );
}
